// ! FIX ME: Refactor for responsive mobile + comment
// ! FIX ME: Multiple notifications stack
import { CloseIcon, ImageButton } from '@evolve/common-ui';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useState } from 'react';
import { useNotificationContext } from '../../contexts/notification.context';
import { TNotification } from '../../hooks/use-notification.hook';
import { cn } from '../../utils';

export const TOAST_SIZES = {
  small: 'max-w-sm',
  medium: 'max-w-md',
  large: 'max-w-lg',
  extraLarge: 'max-w-xl',
  extraLarge2: 'max-w-2xl',
  extraLarge3: 'max-w-3xl',
};

export enum TOAST_TYPES {
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
  DEFAULT = 'default',
}

function NotificationToast(props: TNotification) {
  const { removeNotification } = useNotificationContext();

  const {
    id = Date.now(),
    title,
    message,
    type = TOAST_TYPES.DEFAULT,
    timeout = 5000,
    size = TOAST_SIZES.small,
  } = props;

  const [show, setShow] = useState(true);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        removeNotification(id);
        setShow(false);
      }, timeout);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [show] // Only re-call effect if value or delay changes
  );

  return (
    <div
      data-testid="notification-toast"
      aria-live="assertive"
      className="fixed bottom-0 z-[100] flex items-end w-full px-4 py-6 pointer-events-none set-x-0 right-5 sm:items-start sm:p-6"
    >
      {/* Global notification live region, render this permanently at the end of the document */}
      <div className="flex flex-col items-center w-full space-y-4 sm:items-end">
        {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={cn(
              'w-full overflow-hidden bg-white border-2 shadow-lg pointer-events-auto border-evolve-primary ring-1 ring-black ring-opacity-5',
              size
            )}
          >
            <div className="p-4">
              <div className="flex flex-row items-start justify-between ">
                <div className="py-1">
                  {type === TOAST_TYPES.SUCCESS && (
                    <CheckCircleIcon
                      className="w-6 h-6 text-green-400"
                      aria-hidden="true"
                    />
                  )}
                  {type === TOAST_TYPES.ERROR && (
                    <ExclamationTriangleIcon
                      className="w-6 h-6 text-red-400 "
                      aria-hidden="true"
                    />
                  )}
                  {type === TOAST_TYPES.WARNING && (
                    <ExclamationTriangleIcon
                      className="w-6 h-6 text-yellow-400"
                      aria-hidden="true"
                    />
                  )}
                  {type === TOAST_TYPES.DEFAULT && (
                    <ExclamationCircleIcon
                      className="w-6 h-6 text-blue-400"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <h3 className="w-full ml-3 overflow-hidden text-lg font-bold text-evolve-highlight">
                  {title}
                </h3>
                <div className="ml-4">
                  <ImageButton
                    active
                    icon={<CloseIcon />}
                    onClickHandler={() => {
                      removeNotification(id);
                      setShow(false);
                    }}
                    tooltip="Fechar"
                  />
                </div>
              </div>
              <div className="w-full p-4 mt-3 bg-evolve-shadow">
                <p className="text-md text-evolve-highlight ">{message}</p>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}

// * Render every notification on notifications list
export function Notifications() {
  const { notifications } = useNotificationContext();

  return notifications.map((notification: TNotification) => (
    <NotificationToast key={notification.id} {...notification} />
  ));
}
